import { NavLink } from 'react-router-dom'
import { FilterMatchMode } from 'primereact/api'
import { ButtonGroup } from 'react-bootstrap'
import { useState } from 'react'
import DeleteDialog from '../../../../common/dialogs/DeleteDialog'
import PageHeader from '../../../../layout/page/PageHeader'
import DataTable from '../../../../common/data-table/DataTable'
import TrueFalseCell from '../../../../common/data-table/TrueFalseCell'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit } from '@fortawesome/free-solid-svg-icons'

type TableRow = Record<string, unknown> & { id?: number; full_name?: string; media_store_id?: number; pages_name?: string }
type StatusLabels = { trueLabel: string; falseLabel: string }

const TeamMembers = () => {
    const [refreshTable, setRefreshTable] = useState(false)

    const actionsBodyTemplate = (rowData: TableRow) => {
        return (
            <ButtonGroup size="sm">
                <DeleteDialog
                    url={'/team-members/delete/' + rowData.id}
                    record={rowData}
                    deletedCallback={() => setRefreshTable(true)}
                    message={
                        <p className="mb-3 pb-3 border-bottom">
                            Are you sure you want to delete{' '}
                            <strong> {rowData.full_name}</strong>?
                        </p>
                    }
                />
                <NavLink
                    to={'/people/team/members/view/' + rowData.id}
                    className="btn btn-outline-primary ms-1"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </NavLink>
            </ButtonGroup>
        )
    }

    const imageBodyTemplate = (rowData: TableRow) => {
        return (
            <>
                {rowData.media_store_id ? (
                    <img
                        src={`${process.env.REACT_APP_API_ROOT_URL}/media/download/${rowData.media_store_id}/42`}
                        alt={rowData.pages_name}
                        className="w-6rem shadow-2 border-round"
                        style={{ maxWidth: 42 }}
                    />
                ) : (
                    <div>
                        <FontAwesomeIcon
                            icon={['far', 'image']}
                            className="text-muted fa-2x"
                        />
                    </div>
                )}
            </>
        )
    }

    const statusTemplate = (rowData: TableRow, field: string, labels: StatusLabels) => {
        return (
            <TrueFalseCell
                value={rowData[field] === 1}
                trueLabel={labels.trueLabel}
                falseLabel={labels.falseLabel}
            />
        )
    }

    return (
        <>
            <PageHeader
                icon="users"
                subtitle="Manage your team members and their roles"
                gradient="linear-gradient(135deg, #3b82f6, #2563eb)"
                header="Team Members"
                buttons={[
                    {
                        variant: 'success',
                        label: 'Create New',
                        icon: 'fa-solid fa-plus',
                        url: '/people/team/members/new',
                    },
                ]}
            />
            <DataTable
                refreshTable={refreshTable}
                onRefreshed={() => setRefreshTable(false)}
                config={{
                    dataRoute: '/team-members',
                    first: 0,
                    rows: 10,
                    page: 0,
                    sortField: 'full_name',
                    sortOrder: 1,
                    filters: {
                        global: {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        full_name: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        department_name: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        position: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                    },
                    filterDisplay: 'menu',
                }}
                columns={[
                    {
                        field: 'media_store_id',
                        header: 'Image',
                        sortable: true,
                        body: imageBodyTemplate,
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '6.5rem',
                            minWidth: '6.5rem',
                            maxWidth: '6.5rem',
                        },
                        style: {
                            width: '6.5rem',
                            minWidth: '6.5rem',
                            maxWidth: '6.5rem',
                        },
                    },
                    {
                        field: 'full_name',
                        header: 'Full Name',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                    },
                    {
                        field: 'position',
                        header: 'Position',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        headerStyle: {
                            maxWidth: '3rem',
                        },
                        style: {
                            maxWidth: '3rem',
                        },
                    },
                    {
                        field: 'department_name',
                        header: 'Department',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        headerStyle: {
                            maxWidth: '3rem',
                        },
                        style: {
                            maxWidth: '3rem',
                        },
                    },
                    {
                        field: 'show_on_web',
                        header: 'On Website',
                        sortable: true,
                        filter: false,
                        body: (rowData: TableRow) =>
                            statusTemplate(rowData, 'show_on_web', {
                                trueLabel: 'Shown on Website',
                                falseLabel: 'Hidden from Website',
                            }),
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '10rem',
                            minWidth: '10rem',
                            maxWidth: '10rem',
                        },
                        style: {
                            width: '10rem',
                            minWidth: '10rem',
                            maxWidth: '10rem',
                        },
                    },
                    {
                        field: 'id',
                        header: 'Actions',
                        sortable: false,
                        body: actionsBodyTemplate,
                        alignHeader: 'center',
                        headerStyle: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        style: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        toggleVisibility: false,
                    },
                ]}
                globalFilterFields={[
                    'full_name',
                    'position',
                    'department_name',
                ]}
            />
        </>
    )
}

export default TeamMembers
